
import { lifecycleSubject } from '@/service/upload/rx/lifecycleSignal';
import { filter, take } from 'rxjs/operators';
import store from '@/store';
import PhotosModule, { Conflict } from '@/store/modules/Photos';
import SessionsModule from '@/store/modules/Sessions';
import UploadModule from '@/store/modules/Upload';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import { replaceBus } from './ReplaceDialog.vue';

const uploadModule = getModule(UploadModule, store);
const sessionsModule = getModule(SessionsModule, store);
const photosModule = getModule(PhotosModule, store);

@Component
export default class FinalizePhotos extends Vue {
    private conflictOverrides: Conflict[] = [];
    private activeConflictOverride = false;

    @Watch('originalConflicts')
    originalConflictsChange() {
        this.conflictOverrides = this.conflictOverrides.filter(
            c => this.originalConflicts.some(oc => oc.conflictId === c.conflictId)
        );
    }

    get originalConflicts() {
        return uploadModule.conflicts;
    }

    get conflicts() {
        return this.originalConflicts.map(conflict => {
            const conflictOverride = this.conflictOverrides.find(c => c.conflictId === conflict.conflictId);
            if (conflictOverride)
                return conflictOverride;

            return conflict;
        })
    }

    get conflictsWithSessions() {
        return this.conflicts.map(conflict => ({
            ...conflict,
            session: sessionsModule.sessions[conflict.sessionId],
        }));
    }

    get nonFinalizationConflicts() {
        return this.conflictsWithSessions.filter(({ session }) => !session.finalizationMode);
    }

    get finalizationConflicts() {
        return this.conflictsWithSessions.filter(({ session }) => session.finalizationMode);
    }

    get dialogOpen() {
        return this.finalizationConflicts.length > 0 && !this.activeConflictOverride;
    }

    replaceAll() {
        this.conflicts.forEach(conflict => this.replaceAndFinalize(conflict));
    }

    addAll() {
        this.conflicts.forEach(conflict => this.add(conflict));
    }

    async replaceAndFinalize(conflict: Conflict) {
        conflict.resolve(conflict.conflicted.id);
        this.removeConflict(conflict);
        try {
            await new Promise<void>((resolve, reject) => {
                lifecycleSubject.pipe(
                    filter(({ file }) => file === conflict.photo.file),
                    take(1),
                ).subscribe({
                    next({ file, lifecycleEvent }) {
                        if (file !== conflict.photo.file) return;
                        if (lifecycleEvent === 'finish') {
                            resolve();
                        } else if (lifecycleEvent === 'error' || lifecycleEvent === 'cancel') {
                            reject();
                        }
                    }
                })
            });
            Vue.nextTick(() => 
                photosModule.finalize(conflict.conflicted.id)
            );
        } catch (_) {}
    }

    requestConflictOverride(conflict: Conflict) {
        replaceBus.$emit('requestConflictOverride', conflict);
    }

    add(conflict: Conflict) {
        conflict.resolve();
        this.removeConflict(conflict);
    }

    cancel(conflict: Conflict) {
        uploadModule.cancelUpload({ file: conflict.photo.file });
        this.removeConflict(conflict);
    }

    cancelAll() {
        this.conflicts.forEach(conflict => this.cancel(conflict));
    }

    mounted() {
        replaceBus.$on('requestConflictOverride', () => {
            this.activeConflictOverride = true;
        });

        replaceBus.$on('resolveConflictOverride', (conflict: Conflict) => {
            this.conflictOverrides = [
                ...this.conflictOverrides.filter(c => c.conflictId !== conflict.conflictId),
                conflict,
            ];
            this.activeConflictOverride = false;
        });
    }

    async overrideConflict(conflict: Conflict) {
        replaceBus.$emit('requestConflictOverride', conflict);
        // const newConflict = await new Promise<Conflict>(resolve => {
        //     replaceBus.$once('resolveConflictOverride', resolve);
        // });

        // if (newConflict) {
        //     this.conflictOverrides.push(newConflict);
        // }
    }

    private removeConflict(conflict: Conflict) {
        this.conflictOverrides = this.conflictOverrides.filter(
            c => c.conflictId !== conflict.conflictId
        );
        uploadModule.removeConflict(conflict);
    }
}
